// Employee Performance section — FE-033 / spec §7.0.4.
//
// Owns the table state (sort + offset) so the React-Query hook key
// changes deterministically per click. Default sort matches the
// BE-029 service: ``past_due`` desc.

import { useState } from "react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useDashboardDateRange,
  useEmployeePerformanceQuery,
} from "@/features/dashboard";

import {
  EmployeePerformanceTable,
  type EmployeePerformanceSortKey,
} from "./EmployeePerformanceTable";


export const EMPLOYEE_PERFORMANCE_SECTION = "employee-performance" as const;
export const EMPLOYEE_PERFORMANCE_SPAN_CLASS = "xl:col-span-2 md:col-span-2";


const DEFAULT_LIMIT = 10;
const DEFAULT_SORT_BY: EmployeePerformanceSortKey = "past_due";
const DEFAULT_SORT_DIR: "asc" | "desc" = "desc";


export function EmployeePerformanceSection() {
  const filters = useCommonFilters();
  const dateRange = useDashboardDateRange();
  const [sortBy, setSortBy] =
    useState<EmployeePerformanceSortKey>(DEFAULT_SORT_BY);
  const [sortDir, setSortDir] =
    useState<"asc" | "desc">(DEFAULT_SORT_DIR);
  const [offset, setOffset] = useState(0);

  const query = useEmployeePerformanceQuery({
    ...filters,
    sortBy,
    sortDir,
    limit: DEFAULT_LIMIT,
    offset,
  });

  return (
    <DashboardCard
      section={EMPLOYEE_PERFORMANCE_SECTION}
      className={EMPLOYEE_PERFORMANCE_SPAN_CLASS}
      title="Employee Performance"
      subtitle="Completed, points, completion rate, on-time, and past-due per staff"
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="table" rows={5} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Employee Performance"
          retry={() => query.refetch()}
        />
      ) : query.data && query.data.rows.length > 0 ? (
        <EmployeePerformanceTable
          rows={query.data.rows}
          total={query.data.total}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={({ sortBy: nextBy, sortDir: nextDir }) => {
            setSortBy(nextBy);
            setSortDir(nextDir);
            setOffset(0);
          }}
          limit={DEFAULT_LIMIT}
          offset={offset}
          onOffsetChange={setOffset}
          dateRange={dateRange}
        />
      ) : (
        <DashboardEmptyState
          title="No Employee Performance data yet"
          description="Once Commitments are completed, per-staff metrics appear here."
        />
      )}
    </DashboardCard>
  );
}
